import { withinScopeDates, type PointsScope } from './definition.ts';
import type { ScopeEventOption } from './ScopeEditor';

/**
 * §9.7 — "Preview with zero events in scope → explain which rule excluded everything."
 *
 * An empty board is the single most confusing state this screen has, because every possible cause
 * looks identical: no standings. The rules are also ordered (§3.2), so the *first* one that
 * empties the set is the one worth naming — reporting all of them sends an operator to fix
 * something that was never the problem.
 *
 * <b>The server's count is the input, not this function's opinion.</b> This is only called when
 * the preview came back with `eventsInScope: 0`, and it walks the same rules in the same order
 * over the events the screen has loaded. That is what makes the last branch sound: if candidates
 * survive every rule this can evaluate and the server still found none, the only rule left is the
 * minimum field size — which the client cannot check itself, because the server counts re-entries
 * the tournament list does not report.
 */
export function diagnoseEmptyScope(
  scope: PointsScope,
  /**
   * The room's events, or null while they are being read or when the read failed (known gap 112) — which is
   * not a room with none, and must not be told it is.
   */
  events: readonly ScopeEventOption[] | null,
  /** Whether `events` is a capped slice, in which case claims about "no event" are hedged. */
  truncated = false,
  /** Whether a null `events` is a read that failed rather than one still on its way. */
  failed = false,
): string {
  if (events === null) {
    return failed
      ? "This room's events couldn't be read, so which rule left nothing to score can't be said. Reload to try again."
      : "Still reading this room's events, so which rule left nothing to score can't be said yet.";
  }

  if (events.length === 0) {
    return 'This room has no tournaments recorded yet, so there is nothing to score.';
  }

  const none = truncated ? 'None of the tournaments loaded here' : 'No tournament in this room';

  // An explicit include overrides every rule but room ownership, so a non-empty list that still
  // yields nothing means those events belong to a different room.
  if (scope.includeEventIds.length > 0) {
    return `${scope.includeEventIds.length} event${
      scope.includeEventIds.length === 1 ? ' is' : 's are'
    } on the “always scores” list, but the server found none of them in this room. Scope never crosses rooms — an event from another room can’t be pulled in.`;
  }

  /*
   * §4.2's bounds, either of which may be open — through `withinScopeDates`, the one comparison
   * §9.9's candidate list also runs, so the rule this sentence blames and the rule that filtered
   * the list cannot come apart.
   *
   * <b>An open bound can never be the culprit</b>, which is why the message is skipped rather than
   * merely reworded when there are none. Written inline the comparison silently dropped every
   * event (JS coerces a null bound to 0 and compares it against a string), so the diagnosis blamed
   * the dates for a tag rule and printed "falls between null and null" at an operator — sending
   * them to widen a range that was already infinite while the rule that really emptied the set went
   * unmentioned.
   */
  const bounded = scope.startDate !== null || scope.endDate !== null;
  const inRange = events.filter((event) => withinScopeDates(event.date, scope));

  if (bounded && inRange.length === 0) {
    return `${none} falls ${scopeRange(scope)}. Widen the dates in Test scope.`;
  }

  if (scope.includeTags.length > 0) {
    const tagged = inRange.filter((event) => hasAny(event.tags, scope.includeTags));
    if (tagged.length === 0) {
      return `${inRange.length} event${inRange.length === 1 ? '' : 's'} fall in the date range, but none carries ${list(
        scope.includeTags,
      )}. An event needs at least one of the tags you’ve required.`;
    }
  }

  const required =
    scope.includeTags.length > 0
      ? inRange.filter((event) => hasAny(event.tags, scope.includeTags))
      : inRange;

  if (scope.excludeTags.length > 0) {
    const surviving = required.filter((event) => !hasAny(event.tags, scope.excludeTags));
    if (surviving.length === 0) {
      return `Every event in range carries ${list(scope.excludeTags)}, which you’ve set to never score.`;
    }
  }

  const surviving = (
    scope.excludeTags.length > 0
      ? required.filter((event) => !hasAny(event.tags, scope.excludeTags))
      : required
  ).filter((event) => !scope.excludeEventIds.includes(event.id));

  if (surviving.length === 0) {
    return 'Every event that would otherwise qualify is on the “never scores” list.';
  }

  // Everything this screen can check leaves events standing, and the server still found none —
  // so it is the one rule left. Worth saying explicitly that the server's count of a field is
  // not the entry count shown elsewhere.
  if (scope.minFieldSize > 0) {
    return `Nothing reaches a field of ${scope.minFieldSize}. Field size counts every entry, including re-entries, so it is at least the number of players.`;
  }

  return 'Nothing matched. Check the dates and tags in Test scope — an event has to satisfy all of them at once.';
}

/**
 * The date rule as a phrase, with an open edge left open rather than printed as `null`. Three
 * arrangements and not four: this is only reached when at least one bound is set.
 */
function scopeRange(scope: PointsScope): string {
  if (scope.startDate === null) return `on or before ${scope.endDate}`;
  if (scope.endDate === null) return `on or after ${scope.startDate}`;
  return `between ${scope.startDate} and ${scope.endDate}`;
}

function hasAny(tags: readonly string[], wanted: readonly string[]): boolean {
  const lower = new Set(tags.map((t) => t.toLowerCase()));
  return wanted.some((t) => lower.has(t.toLowerCase()));
}

function list(values: readonly string[]): string {
  if (values.length === 1) return `“${values[0]}”`;
  if (values.length === 2) return `“${values[0]}” or “${values[1]}”`;
  return `${values.slice(0, -1).map((v) => `“${v}”`).join(', ')} or “${values[values.length - 1]}”`;
}
